import React, { useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Menu, MenuItem, Button } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const PANEL_TYPES = {
    TEXT: 'text',
    TABLE: 'table',
    FILES: 'files',
    USERS: 'users',
    CUSTOM: 'custom',
    JUDGING_DASHBOARD: 'judging_dashboard'
};

const CustomPanel = ({ panel, onEdit, onDelete, onDragStart, eventDetails }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleEdit = () => {
        handleMenuClose();
        if (onEdit) onEdit(panel);
    };

    const handleDelete = () => {
        handleMenuClose();
        if (onDelete) onDelete(panel.id);
    };

    const renderPanelContent = () => {
        switch (panel.type) {
            case PANEL_TYPES.TEXT:
                return (
                    <Typography variant="body2" color="text.secondary">
                        {panel.description?.trim() ? panel.description : <span style={{ color: '#bbb' }}>No content yet.</span>}
                    </Typography>
                );

            case PANEL_TYPES.TABLE:
                return (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {panel.tableConfig.columns.map((column) => (
                                        <TableCell key={column.id}>{column.name}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {panel.tableConfig.rows.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        {panel.tableConfig.columns.map((column) => (
                                            <TableCell key={column.id}>{row[column.id]}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );

            case PANEL_TYPES.FILES:
                return (
                    <Box sx={{ mt: 2 }}>
                        {panel.files.map((file, index) => (
                            <Chip
                                key={index}
                                label={file.name}
                                sx={{ m: 0.5 }}
                            />
                        ))}
                    </Box>
                );

            case PANEL_TYPES.CUSTOM:
                return (
                    <Box>
                        {panel.sections.map((section) => (
                            <Box key={section.id} sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    {section.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {section.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                );

            case PANEL_TYPES.JUDGING_DASHBOARD:
                const handleOpenJudgingDashboard = () => {
                    // Pass event data via URL parameters
                    const eventData = {
                        eventId: eventDetails?.event?.eventId,
                        eventName: eventDetails?.event?.title,
                        eventDate: eventDetails?.event?.eventStartDate,
                        guestCount: eventDetails?.event?.guestCount,
                        eventVision: eventDetails?.event?.eventVision
                    };
                    
                    // Store data in localStorage for the external system to access
                    localStorage.setItem('hackathonEventData', JSON.stringify(eventData));
                    
                    // Create URL with parameters
                    const baseUrl = 'https://graceful-squirrel-520c02.netlify.app/';
                    const params = new URLSearchParams({
                        eventId: eventData.eventId || '',
                        eventName: eventData.eventName || '',
                        eventDate: eventData.eventDate || '',
                        guestCount: eventData.guestCount || '',
                        eventVision: eventData.eventVision || ''
                    });
                    
                    const fullUrl = `${baseUrl}?${params.toString()}`;
                    window.open(fullUrl, '_blank');
                };

                return (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Manage your hackathon participants, judging criteria, and results with our comprehensive dashboard.
                        </Typography>
                        
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                ✨ Key Features:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Participant registration & management
                                </Typography>
                                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Custom judging criteria setup
                                </Typography>
                                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Real-time scoring & leaderboards
                                </Typography>
                                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Automated winner announcements
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleOpenJudgingDashboard}
                                sx={{ 
                                    '&:hover': { backgroundColor: '#1565c0' }
                                }}
                            >
                                Open Judging Dashboard
                            </Button>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, minHeight: 120, position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box className="panel-drag-handle" sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', userSelect: 'none' }}>
                    <DragIndicatorIcon sx={{ mr: 1, color: '#b3c6e0' }} fontSize="small" />
                </Box>
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>{panel.title || 'Untitled Panel'}</Typography>
                <IconButton onClick={handleMenuOpen} size="small">
                    <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                    <MenuItem onClick={handleEdit}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        Edit
                    </MenuItem>
                    <MenuItem onClick={handleDelete}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        Delete
                    </MenuItem>
                </Menu>
            </Box>
            <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                {renderPanelContent()}
            </Box>
        </Box>
    );
};

export default CustomPanel; 